import * as analytics from 'analytics';
import invariant from 'invariant';
import { isChrome } from 'util/device';
import graphql from 'api/graphql';
import { fetchGigyaJWTPromise } from 'api/gigya';
import { isHuawei } from 'util/device';

import { EVENT_TYPE, TIME } from 'globalConst/const';

import AudioPlayer from './AudioPlayer';
import { logger } from '../util/index';

class AudioPlayerOnDemand extends AudioPlayer {
  constructor() {
    super();
    this.currentTime = 0;
    this.trackProgress = false;
    this.previousClip = null;
    this.playbackTimeToSet = null;

    this.audioElement.addEventListener('loadedmetadata', this.onLoadedmetadata.bind(this));
  }

  progressHeartbeat = {
    interval: null,
    start: () => {
      clearInterval(this.progressHeartbeat.interval);
      this.progressHeartbeat.interval = setInterval(() => this.saveProgress(EVENT_TYPE.HEARTBEAT), TIME.HEARTBEAT);
    },
    stop: () => clearInterval(this.progressHeartbeat.interval),
  };

  /**
   * Initialises and load an AudioPlayer using the onDemandClip
   */
  async load(onDemandPlaybackTime) {
    await this.initialisePlayer(this.onDemandClip.audioUrl);

    try {
      const { imageUrl } = this.onDemandClip;

      if (this.onDemandClip.show) {
        const { name } = this.onDemandClip.show.station;
        this.setMediaSessionData(name, imageUrl);
      }

      if (this.track) {
        const { title } = this.onDemandClip;
        this.setMediaSessionData(title, imageUrl);
      }

      if (onDemandPlaybackTime) {
        this.playbackTimeToSet = onDemandPlaybackTime;
      }

      // Huawei QuickApp uses the native android player, if we start it here there are two players active
      if (!isHuawei) {
        await this.audioElement.play();
      }
    } catch (error) {
      // Don't propagate the error if the user aborts the load (ABORT_ERR)
      if (error.code !== DOMException.ABORT_ERR) {
        this.errorCallback(
          this.onDemandClip.audioUrl.includes('http://') && isChrome()
            ? {
                name: 'MixedContent',
                message: 'http source is used',
              }
            : error,
          this.onDemandClip,
          this.playSessionId
        );
      }
    }
  }

  onLoadedmetadata() {
    if (this.playbackTimeToSet) {
      this.audioElement.currentTime = this.playbackTimeToSet;
      this.playbackTimeToSet = null;
    }

    const { currentTime, duration } = this.audioElement;
    analytics.trackPlayOnDemandClip(this.onDemandClip, currentTime, duration);
  }

  /**
   * Binds the given callback to the audioElement's ended event
   * @param {function} callback
   */
  endedPlay(callback) {
    this.audioElement.addEventListener('ended', () => {
      callback();
      this.saveProgress(EVENT_TYPE.STOP);
    });
  }

  /**
   * Resumes audio playback
   */
  async resume() {
    try {
      invariant(this.playSessionId && this.onDemandClip, 'PlaySessionId and onDemandClip should both be set.');

      // Huawei QuickApp uses the native android player, if we start it here there are two players active
      if (!isHuawei) {
        await this.audioElement.play();
      }

      const { currentTime, duration } = this.audioElement;
      analytics.trackResumeOnDemand(this.onDemandClip, currentTime, duration);

      this.healthyPlayDetector.resume();
    } catch (error) {
      this.errorCallback(error, this.playSessionId);
      logger.debug('Swallowed an error while calling resume()', error.code, error);
    }
  }

  /**
   * Plays audio from given onDemandClip
   * @param {string} playSessionId
   * @param {object} onDemandClip
   */
  async play(playSessionId, onDemandClip, onDemandPlaybackTime, trackProgress) {
    this.trackProgress = trackProgress;

    const { currentTime } = this.audioElement;
    // Resume playback if it's the same sesseion
    if (this.playSessionId === playSessionId) {
      if (currentTime !== onDemandPlaybackTime) {
        this.audioElement.currentTime = onDemandPlaybackTime;
      }

      await this.resume();
    } else {
      // Set and load content on new session
      this.playSessionId = playSessionId;
      this.previousClip = this.onDemandClip
        ? {
            onDemandClipId: this.onDemandClip.id,
            progress: this.audioElement.currentTime,
          }
        : null;
      this.onDemandClip = onDemandClip;

      const { durationLength = 0, contentLength = 0 } = this.onDemandClip.progress || {};

      const isCompleted = (durationLength / contentLength) * 100 > 95;

      if (trackProgress && this.onDemandClip.progress && !isCompleted) {
        onDemandPlaybackTime = this.onDemandClip.progress.durationLength || 0;
        analytics.trackResumeOnDemand(
          this.onDemandClip,
          onDemandPlaybackTime,
          this.onDemandClip.progress.contentLength
        );
      } else {
        analytics.trackStartOnDemandClip(this.onDemandClip);
      }

      await this.load(onDemandPlaybackTime);

      this.healthyPlayDetector.startNew();
    }

    this.saveProgress(EVENT_TYPE.START);
  }

  /**
   * Binds the given callback to the audioElement's durationChanged event
   * @param {function} callback
   */
  durationChanged(callback) {
    this.audioElement.addEventListener(
      'durationchange',
      (event) => {
        callback(event.target.duration);
      },
      true
    );
  }

  /**
   * Binds the given callback to the audioElement's emptied event
   * @param {function} callback
   */
  emptied(callback) {
    this.audioElement.addEventListener('emptied', () => {
      this.saveProgress(EVENT_TYPE.STOP, this.previousClip);
      callback();
    });
  }

  /**
   * Sets the callback triggered after a progress stop event
   * @param {function} callback
   */
  setProgressSentCallback(callback) {
    this.progressSentCallback = callback;
  }

  /**
   * Moves the audio playback forward using the given skipTime
   * @param {number} skipTime
   */
  skipForward(skipTime) {
    const { currentTime, duration } = this.audioElement;
    if (!currentTime) return;

    this.saveProgress(EVENT_TYPE.STOP);

    this.audioElement.currentTime = Math.min(currentTime + skipTime, duration);

    this.saveProgress(EVENT_TYPE.START);

    if (this.onDemandClip) {
      analytics.trackSkipForwardOnDemand(skipTime, this.onDemandClip, currentTime, duration);
    }
  }

  /**
   *  Moves the audio playback backward using the given skipTime
   * @param {number} skipTime
   */
  skipBackward(skipTime) {
    const { currentTime, duration } = this.audioElement;
    if (!currentTime) return;

    this.saveProgress(EVENT_TYPE.STOP);

    this.audioElement.currentTime = Math.max(currentTime - skipTime, 0);

    this.saveProgress(EVENT_TYPE.START);

    if (this.onDemandClip) {
      analytics.trackSkipBackwardOnDemand(skipTime, this.onDemandClip, currentTime, duration);
    }
  }

  /**
   *  Moves the audio playback to the given skipTime
   * @param {number} skipTime
   * @param {boolean} [trackAnalytics=true]
   */
  skipTo(skipTime, trackAnalytics = true) {
    const { currentTime, duration } = this.audioElement;

    if (!currentTime) return;

    this.saveProgress(EVENT_TYPE.STOP);

    this.audioElement.currentTime = skipTime;

    this.saveProgress(EVENT_TYPE.START);

    if (this.onDemandClip && trackAnalytics) {
      analytics.trackSkipToOnDemand(skipTime, this.onDemandClip, currentTime, duration);
    }
  }

  /**
   * Stops the audio playback
   */
  stop(trackAnalytics = true) {
    const { currentTime, duration } = this.audioElement;
    super.stop();

    if (trackAnalytics) {
      // Stop any heartbeat that is active
      analytics.stopHeartBeat();

      if (this.onDemandClip) analytics.trackStopOnDemand(this.onDemandClip, currentTime, duration);
    }

    this.saveProgress(EVENT_TYPE.STOP);
  }

  /**
   * Saves progress of the audio clip to API via GraphQL
   * @param {string} type event type
   * @param {string} [manualData] override derriving the data and provide it manually
   */
  async saveProgress(type, manualData = null) {
    if (!this.trackProgress || !this.onDemandClip) return;

    try {
      const { paused } = this.audioElement;
      let id;
      let currentTime;

      if (manualData) {
        id = manualData.onDemandClipId;
        currentTime = manualData.progress;
      } else {
        ({ id } = this.onDemandClip);
        ({ currentTime } = this.audioElement);
      }

      if (type === EVENT_TYPE.STOP || (type === EVENT_TYPE.START && paused)) this.progressHeartbeat.stop();
      if (type === EVENT_TYPE.START && !paused) this.progressHeartbeat.start();

      const jwtToken = await fetchGigyaJWTPromise();

      await graphql.mutateProgress(jwtToken, id, type, Math.floor(currentTime));

      // Make sure to fetch latest progress
      if (type === EVENT_TYPE.STOP) this.progressSentCallback();
    } catch (error) {
      logger.error(`Something went wrong while saving ${type} progress for onDemandClip:`, error.message);
    }
  }
}

const audioPlayerOnDemand = new AudioPlayerOnDemand();

export default audioPlayerOnDemand;
